import { Suspense } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { openRoutes } from '@/configs/routes.config/routes.config'
import Loading from '@/components/shared/Loading'
import PlanActivationWatcher from '@/components/shared/PlanActivationWatcher'
import QuotaDialog from '@/components/shared/QuotaDialog'
import UsageBanner from '@/components/shared/UsageBanner'
import PlanDialog from '@/views/settings/Billing/components/PlanDialog'
import type { CommonProps } from '@/@types/common'
import { useAuth } from '@/auth'
import { useThemeStore } from '@/store/themeStore'
import PostLoginLayout from './PostLoginLayout'
import PreLoginLayout from './PreLoginLayout'

const Layout = ({ children }: CommonProps) => {
    const layoutType = useThemeStore((state) => state.layout.type)

    const { authenticated } = useAuth()

    // The open routes (openRoute.ts) are pages shown to strangers — a public
    // booking page, an invitation. A signed-in host previewing their own
    // booking page must see what an invitee sees, so the billing chrome
    // stays off them.
    const { pathname } = useLocation()
    const open = openRoutes.some((route) => matchPath(route.path, pathname))

    return (
        <Suspense
            fallback={
                <div className="flex flex-auto flex-col h-[100vh]">
                    <Loading loading={true} />
                </div>
            }
        >
            {authenticated ? (
                <PostLoginLayout layoutType={layoutType}>
                    {/* Both are billing-wide and belong above the route tree:
                        a quota refusal can come from any page, and a usage
                        warning should not vanish when the user navigates. */}
                    {!open && <UsageBanner />}
                    {children}
                    {!open && (
                        <>
                            <QuotaDialog />
                            {/* Opened from the drawer's plan item. Here rather
                                than in the drawer, which unmounts as it
                                closes. */}
                            <PlanDialog />
                            <PlanActivationWatcher />
                        </>
                    )}
                </PostLoginLayout>
            ) : (
                <PreLoginLayout>{children}</PreLoginLayout>
            )}
        </Suspense>
    )
}

export default Layout
